import { Component, ChangeDetectionStrategy, Input, EventEmitter, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '@mode/capra';
import { MatLegacyAutocompleteModule } from '@angular/material/legacy-autocomplete';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { Observable, debounceTime, map } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { SlackChannel } from '@mode/shared/contract-common';
import { getAppName } from '@mode/shared/util-js';

export interface SlackForm {
  channel: SlackChannel;
}

export interface SlackChannelDetails {
  slackAppUrl: string;
  slackAppTeamName: string;
  slackChannels: Array<SlackChannel> | [];
}

@Component({
  standalone: true,
  selector: 'mode-slack-channel-selector',
  imports: [CommonModule, IconComponent, MatLegacyAutocompleteModule, ReactiveFormsModule, AsyncPipe],
  templateUrl: './slack-channel-selector.component.html',
  styleUrls: ['./slack-channel-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SlackChannelSelectorComponent implements OnInit {
  constructor(private _fb: UntypedFormBuilder) {}
  @Input() slackChannelDetails: SlackChannelDetails = {
    slackAppUrl: '',
    slackAppTeamName: '',
    slackChannels: [],
  };
  @Input() selectedSlackChannel?: SlackChannel | string = '';
  @Output() setSlackChannel = new EventEmitter<SlackChannel>();

  public slackForm = this._fb.group({
    channel: this._fb.control('', [Validators.required, this._channelNameValidator()]),
  });

  filteredChannels$ = this.slackForm.valueChanges.pipe(
    debounceTime(500),
    this._handleEmptyChannel,
    map((value) => {
      this.updateSlackChannel(value);
      return this._filter(value);
    })
  );

  ngOnInit(): void {
    if (this.selectedSlackChannel != '') {
      this.slackForm.controls['channel'].setValue(this.selectedSlackChannel);
    }
  }

  public get channelControl(): UntypedFormControl {
    return this.slackForm.controls['channel'] as UntypedFormControl;
  }

  displayChannelName(channel: SlackChannel): string {
    return channel?.name || '';
  }

  updateSlackChannel(value: any) {
    const channel = this._formatChannelStructure(value);
    this.setSlackChannel.emit(channel);
  }

  private _formatChannelStructure(channel: SlackChannel | string): SlackChannel {
    if (typeof channel === 'string') {
      return {
        name: channel,
        channelName: this._appendLeadingPound(channel),
        id: '',
        type: 'public',
      };
    }
    return channel;
  }

  private _channelNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const channelName = control.value?.name ?? control.value;
      // allowed example "some-slack_channel"
      const regex = /(^[a-zA-Z0-9_-]+$)|(^$)/;
      const isValid = regex.test(channelName);
      return isValid ? null : { invalidText: { value: channelName } };
    };
  }

  private _appendLeadingPound(text: string): string {
    return text.length ? '#' + text : text;
  }

  private _normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '');
  }

  private _filter(value: string | SlackChannel): SlackChannel[] | [] {
    const name = typeof value === 'string' ? value : value?.name;
    const normalizedName = this._normalizeValue(name);
    const filteredChannel = this.slackChannelDetails.slackChannels?.filter((channel: SlackChannel) => {
      if (channel?.name) {
        return this._normalizeValue(channel.name).includes(normalizedName);
      } else {
        return [];
      }
    });

    return filteredChannel;
  }

  private _handleEmptyChannel(source: Observable<SlackForm | string>) {
    return source.pipe(map((value) => (typeof value === 'string' ? '' : value.channel)));
  }
  readonly appName = getAppName();
}
